<template>
  <div class="dashboard_page_container">
    <a :href="warningUrl">
      <div class="warning" v-if="isWarningVisible">
        <b>Atención: </b>{{ warningMessage }}
      </div>
    </a>
    <div class="filters-container">
      <input type="text" placeholder="Buscar por código" v-model="filterText" :class="isMobileRedeem ? 'input-text-mobile' : 'input-text'" maxlength="16" @input="validarInput"/>
      <div style="width: 30px;"></div>
      <div @click="reservationsToShow = 0" class="clickable">
        <template v-if="isMobileRedeem">
          <img 
            :src="reservationsToShow == 0 ? require('@/assets/images/ic_pending_on.png') : require('@/assets/images/ic_pending_off.png')"
            alt="Reservas" 
            class="filter-icon" 
            style="margin-right: 5px;"
          />
        </template>
        <template v-else>
          <span class="header_text" :class="reservationsToShow == 0 ? 'header_text_selected' : 'header_text_unselected'" style="margin-right: 20px;">Reservas</span>
        </template>
      </div>
      <div @click="reservationsToShow = 1" class="clickable">
        <template v-if="isMobileRedeem">
          <img 
            :src="reservationsToShow == 1 ? require('@/assets/images/ic_expired_on.png') : require('@/assets/images/ic_expired_off.png')"
            alt="Caducadas" 
            class="filter-icon" 
            style="margin-right: 5px;"
          />
        </template>
        <template v-else>
          <span class="header_text" :class="reservationsToShow == 1 ? 'header_text_selected' : 'header_text_unselected'"  style="margin-right: 20px;">Caducadas</span>
        </template>
      </div>

      <div @click="reservationsToShow = 2" class="clickable">
        <template v-if="isMobileRedeem">
          <img 
            :src="reservationsToShow == 2 ? require('@/assets/images/ic_redeemed_on.png') : require('@/assets/images/ic_redeemed_off.png')"
            alt="Canjeadas" 
            class="filter-icon" 
            style="margin-right: 5px;"
          />
        </template>
        <template v-else>
          <span class="header_text" :class="reservationsToShow == 2 ? 'header_text_selected' : 'header_text_unselected'"  style="margin-right: 20px;">Canjeadas</span>
        </template>
      </div>

      <div @click="downloadExcelClicked" class="clickable">
        <template v-if="isMobileRedeem">
          <img 
            src="@/assets/images/ic_export.png"
            alt="Descargar informe"
            @click="downloadExcelClicked" 
            class="filter-icon" 
          />
        </template>
        <template v-else>
          <span class="header_text" :class="reservationsToShow == 3 ? 'header_text_selected' : 'header_text_unselected'">Excel</span>
        </template>
      </div>

    </div>
    <table>
      <thead>
        <tr>
          <th class="big">Plan</th>
          <th class="small">Código</th>
          <th class="small">Importe</th>
          <th class="small">Estado</th>
          <th v-if="reservationsToShow !== 2" class="big">Fecha</th>
          <th v-if="reservationsToShow === 2" class="big">Fecha canjeado</th>
        </tr>
      </thead>
      <tbody>
        <tr 
          v-for="(item, index) in reservations
            .filter(reservation => {
              const estado = reservation.estado;
              if (reservationsToShow === 0) return estado === 'Pendiente';
              if (reservationsToShow === 1) return estado === 'Caducado';
              if (reservationsToShow === 2) return estado === 'Canjeado';
              return true;
            })
            .filter(reservation => !filterText || reservation.codigo.toLowerCase().includes(filterText.toLowerCase()))"
          :key="item.codigo"
          :class="index % 2 === 0 ? 'bg-gray-100' : 'bg-white'"
          @click="purchaseClicked(item)"
        >
          <td>{{ item.plan_name }}</td>
          <td>{{ item.codigo }}</td>
          <td>{{ item.importe.toFixed(2) }}€</td>
          <td>{{ item.estado }}</td>
          <td v-if="reservationsToShow !== 2">{{ formatFecha(item.fecha_validez || item.fecha) }}</td>
          <td v-if="reservationsToShow === 2">{{ fechaCanjeadoMax(item) }}</td>
        </tr>
      </tbody>
    </table>
    <ErrorPopup :errorMessage="error" />
    <PurchaseDetailsPopup :purchaseData="selectedPurchaseData" @errorRedeeming="errorRedeeming" @successRedeeming="successRedeeming" />
    <PrivatePlanPurchaseDetailsPopup :privatePlanPurchaseData="selectedPrivatePlanPurchaseData" @errorRedeeming="errorRedeeming" @successRedeeming="successRedeeming" />
    <RedeemSuccessPopup :redeemSuccessVisible="isRedeemSuccessVisible" />
  </div>

</template>

<script setup>
import { ref, onMounted, onBeforeUnmount, onUnmounted } from "vue";
import { useRouter } from 'vue-router';
import { useLoadingStore } from "@/stores/loadingStore";
import { useNavigationStore } from "@/stores/navigationStore";
import { format, parse } from 'date-fns';
import { es } from 'date-fns/locale';
import { refreshToken } from "@/services/authService";

import ErrorPopup from "@/components/common/ErrorPopup.vue";
import PurchaseDetailsPopup from "./common/PurchaseDetailsPopup.vue";
import PrivatePlanPurchaseDetailsPopup from "./common/PrivatePlanPurchaseDetailsPopup.vue";
import RedeemSuccessPopup from "./common/RedeemSuccessPopup.vue";
import * as XLSX from "xlsx";
import { saveAs } from "file-saver";

const error = ref(null);
const reservations = ref([]);
const loadingStore = useLoadingStore();
const navigationStore = useNavigationStore();
const selectedPurchaseData = ref(null);
const selectedPrivatePlanPurchaseData = ref(null);
const filterText = ref("");
const isRedeemSuccessVisible = ref(false);
const router = useRouter();
const isWarningVisible = ref(false);
const warningUrl = ref("");
const warningMessage = ref("");
const reservationsToShow = ref(0); // 0: all, 1: expired, 2: redeemed
const isMobileRedeem = ref(window.innerWidth <= 768)

function handleResizeRedeem() {
  isMobileRedeem.value = window.innerWidth <= 768
}

const formatFecha = (fecha) => {
  let prefix = "";
  if (fecha.toLowerCase().includes("válido hasta: ")) {
    prefix = "Válido hasta: ";
    fecha = fecha.replace(prefix, "");
  }
    const fechaAux = parse(fecha, "dd-MM-yyyy HH:mm", new Date());
    return prefix + format(fechaAux, "dd MMM yyyy", { locale: es });
};

const errorRedeeming = (errorMessage) => {
  error.value = errorMessage;
};

const successRedeeming = () => {
  isRedeemSuccessVisible.value = true;
  getReservations();
};

const getReservations = async () => {
  loadingStore.setLoading(true);
  error.value = null;

  try {
    let response = await fetchReservations();

    if (response.status === 401) {
      const refreshed = await refreshToken();
      if (refreshed) {
        response = await fetchReservations();
      } else {
        loadingStore.setLoading(false);
        localStorage.removeItem('user_id');
        localStorage.removeItem('token');
        router.push('/');
        return;
      }
    }

    if (!response.ok) {
      error.value = "Se ha producido un error obteniendo tus reservas.";
    }

    const data = await response.json();

    if (data.errors) {
      error.value = data.message;
      reservations.value = [];
    } else {
      error.value = null;
      reservations.value = data.datos;
    }
  } catch (exception) {
    error.value = exception.message;
  } finally {
    loadingStore.setLoading(false);
    getStripeStatus();
  }
};

const getStripeStatus = async () => {
  loadingStore.setLoading(true);
  error.value = null;
  warningUrl.value = null;
  warningMessage.value = null;
  isWarningVisible.value = false;

  try {
    let response = await fetchStripeStatus();

    if (response.status === 401) {
      const refreshed = await refreshToken();
      if (refreshed) {
        response = await fetchStripeStatus();
      } else {
        loadingStore.setLoading(false);
        localStorage.removeItem('user_id');
        localStorage.removeItem('token');
        router.push('/');
        return;
      }
    }

    if (!response.ok) {
      error.value = "Se ha producido un error obteniendo el estado de tu cuenta.";
    }

    const data = await response.json();

    navigationStore.setUserType(data.isOptionalCompany ? "optional" : "main");
    if (data.warningMessage != null) {
      warningUrl.value = data.warningUrl;
      warningMessage.value = data.warningMessage;
      isWarningVisible.value = true;
    }
  } catch (exception) {
      // do nothing
  } finally {
    loadingStore.setLoading(false);
  }
};

navigationStore.setNavigationPosition(1);
getReservations();

const validarInput = async () => {
  filterText.value = filterText.value.replace(/[^A-Za-z0-9]/g, '');
}

const purchaseClicked = async (purchase) => {
  isRedeemSuccessVisible.value = false;
  loadingStore.isLoading = true;
  error.value = null;
  selectedPurchaseData.value = null;
  selectedPrivatePlanPurchaseData.value = null;

  try {
    let response = await fetchPurchaseInfo(purchase);

    if (response.status === 401) {
      const refreshed = await refreshToken();
      if (refreshed) {
        response = await fetchPurchaseInfo(purchase);
      } else {
        loadingStore.setLoading(false);
        localStorage.removeItem('user_id');
        localStorage.removeItem('token');
        router.push('/');
        return;
      }
    }

    if (!response.ok) {
      error.value = "Se ha producido un error obteniendo la información de la reserva.";
    }

    const data = await response.json();

    if (data.errors) {
      error.value = data.message;
    } else {
      error.value = null;
      if (data.datos.purchase.tipo == "private") {
        selectedPrivatePlanPurchaseData.value = data.datos;
      } else {
        selectedPurchaseData.value = data.datos;
      }
      history.pushState({ popupOpen: true }, "");
    }
  } catch (exception) {
    error.value = exception.message;
    selectedPurchaseData.value = null;
    selectedPrivatePlanPurchaseData.value = null;
  } finally {
    loadingStore.setLoading(false);
  }
};

async function fetchPurchaseInfo(purchase) {
  return await fetch(process.env.VUE_APP_API_URL+"api/dashboard/redeems/get_purchase_code_info", {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      "vdas": process.env.VUE_APP_API_VDAS,
      "cp-token": process.env.VUE_APP_API_CP_TOKEN,
      "Authorization": "Bearer " + localStorage.getItem('token')
    },
    body: JSON.stringify({
      "user_id": localStorage.getItem('user_id'),
      "purchase_code": purchase.codigo
    }),
  });
}

async function fetchReservations() {
  return await fetch(process.env.VUE_APP_API_URL+"api/dashboard/redeems/get_my_reservations", {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        "vdas": process.env.VUE_APP_API_VDAS,
        "cp-token": process.env.VUE_APP_API_CP_TOKEN,
        "Authorization": "Bearer " + localStorage.getItem('token')
      },
      body: JSON.stringify({
        "user_id": localStorage.getItem('user_id')
      }),
    });
}

async function fetchStripeStatus() {
  return await fetch(process.env.VUE_APP_API_URL+"api/dashboard/stripe/get_account_status", {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      "vdas": process.env.VUE_APP_API_VDAS,
      "cp-token": process.env.VUE_APP_API_CP_TOKEN,
      "Authorization": "Bearer " + localStorage.getItem('token')
    },
    body: JSON.stringify({
      "user_id": localStorage.getItem('user_id')
    }),
  });
}

const handlePopState = () => {
  selectedPurchaseData.value = null;
};

onMounted(() => {
  window.addEventListener("popstate", handlePopState);
    window.addEventListener('resize', handleResizeRedeem)
});

onBeforeUnmount(() => {
  window.removeEventListener('resize', handleResizeRedeem)
});

onUnmounted(() => {
  window.removeEventListener("popstate", handlePopState);
});

const downloadExcelClicked = () => {
  const flatData = [];

  reservations.value.forEach(reserva => {
    reserva.productos.forEach(producto => {
      let fecha = "";
      if (producto.fecha == null || producto.fecha == "") {
        fecha = reserva.fecha_validez;
      } else {
        fecha = producto.fecha + " " + producto.hora;
      }
      // Objeto base
      let entry = {
        "Nombre": reserva.user.name,
        "Email": reserva.user.email,
        "Plan": reserva.plan_name,
        "Código": reserva.codigo,
        "Fecha de Compra": reserva.fecha_compra,
        "Estado": reserva.estado,
        "Producto": producto.titulo,
        "Precio": producto.precio,
        "Gastos de gestión": producto.gastos_gestion,
        "Importe a cobrar": producto.precio - producto.gastos_gestion,
        "Código descuento": reserva.codigo_descuento,
        "Descuento": producto.descuento,
        "Cantidad": producto.cantidad,
        "Cantidad Canjeada": producto.cantidad_canjeada,
        "Fecha de Canjeo": producto.fecha_canjeado,
        "Fecha Producto": fecha
      };

    if (Array.isArray(reserva.datos_adicionales)) {
      reserva.datos_adicionales.forEach(item => {
        if (item.pregunta && item.respuesta !== undefined) {
          const clave = item.pregunta.trim();
          entry[clave] = item.respuesta;
        }
      });
    }

    flatData.push(entry);
    });
  });

  // Crear la hoja de Excel
  const worksheet = XLSX.utils.json_to_sheet(flatData);
  const workbook = XLSX.utils.book_new();
  XLSX.utils.book_append_sheet(workbook, worksheet, "Reservas");

  const excelBuffer = XLSX.write(workbook, { bookType: "xlsx", type: "array" });
  const data = new Blob([excelBuffer], { type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet" });
  saveAs(data, "dashboard.xlsx");
};

const fechaCanjeadoMax = (item) => {
  if (item.tipo == "public") {
    const fechas = item.productos
      .map(p => p.fecha_canjeado)
      .filter(f => f)
      .map(f => parseFecha(f))

    if (!fechas.length) return "Desconocida"

    const maxFecha = new Date(Math.max(...fechas))
    return maxFecha.toLocaleString('es-ES', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  } else {
    return item.fecha_canjeado;
  }
}

const parseFecha = (str) => {
  // Esperado: "dd-MM-yyyy HH:mm"
  const [fecha, hora] = str.split(' ')
  const [dia, mes, anio] = fecha.split('-').map(Number)
  const [horas, minutos] = hora.split(':').map(Number)

  return new Date(anio, mes - 1, dia, horas, minutos)
}

</script>

<style scoped>
table {
  border-radius: 10px;
  overflow: hidden;
  margin-top: 30px;
  width: 100%;
  border-collapse: collapse;
}

th {
  text-align: center;
  padding-top: 10px;
  padding-bottom: 10px;
  background-color: var(--gray);
  color: white;
  border: 0px;
}

.big {
  width: 20%;
}

.small {
  width: 10%;
}

tr{
  cursor: pointer;
}

td {
  text-align: center;
  padding-top: 10px;
  padding-bottom: 10px;
  padding-left: 5px;
  padding-right: 5px;
}

.bg-gray-100 {
  background-color: var(--almost-white);
}

.bg-white {
  background-color: white;
}

.filter-icon {
  cursor: pointer;
  margin-left: 10px;
  width: 30px;
}

.filters-container {
  display: flex;
  justify-content: flex-end;
  margin-top: 20px;
  margin-right: 20px;
  align-items: center;
}

.filters-container .input-text-mobile {
  width: 100%;
  margin-right: 10px;
}

.filters-container .input-text {
  width: 300px;
  margin-right: auto;
}

.warning {
  border-radius: 10px;
  background-color: #ffe00069;
  color: var(--gray);
  margin-top: 30px;
  padding: 10px 20px 8px 20px;
}

.header_icons_container {
  height: 30px;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 25px;
}

.clickable {
  cursor: pointer;
}

.header_text {
  display: flex;
  align-items: center;   /* Centra verticalmente */
  justify-content: center; /* Opcional: centra también horizontalmente */
}

.header_text_selected {
  background-color: var(--gray);
  color: var(--white);
  padding: 10px 15px;
  border-radius: 5px;
}

.header_text_unselected {
  background-color: var(--light-gray);
  color: var(--gray);
  padding: 10px 15px;
  border-radius: 5px;
}

</style>
